import type {
  PdvFlag,
  PdvFlagHistoryEntry,
  PdvFlagIssue,
  PdvFlagReason
} from 'Services/Query/Pdv';

export const pdvMoney = (n = 0) => {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${Math.round(n / 1e3)}k`;
  return `$${Math.round(n)}`;
};

export const pdvDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US') : '';

export const pdvDateTime = (iso?: string | null) =>
  iso
    ? `${pdvDate(iso)} ${new Date(iso).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })}`
    : '';

export const PDV_ISSUE_LABEL: Record<PdvFlagIssue, string> = {
  wrong: 'Wrong value',
  missing: 'Missing',
  other: 'Something else'
};

export const PDV_REASONS: {
  value: PdvFlagReason;
  label: string;
  hint: string;
}[] = [
  {
    value: 'data_correct',
    label: 'Data is correct',
    hint: 'The value on the product is right.'
  },
  {
    value: 'duplicate',
    label: 'Duplicate report',
    hint: 'The same problem is already reported.'
  },
  {
    value: 'not_enough_info',
    label: 'Not enough information',
    hint: "The report doesn't say what's wrong."
  },
  {
    value: 'out_of_scope',
    label: 'Out of scope',
    hint: "It isn't product data, e.g. price or stock."
  }
];

export const pdvReasonLabel = (reason?: PdvFlagReason | null) =>
  PDV_REASONS.find(r => r.value === reason)?.label ?? '';

export type PdvFlagDisplay =
  'pending' | 'in_progress' | 'completed' | 'not_issue';

/** Closed splits in two on screen: fixed reads Completed, not_issue Not an issue. */
export const pdvFlagDisplay = (f: PdvFlag): PdvFlagDisplay => {
  if (f.status === 'pending') return 'pending';
  if (f.status === 'in_progress') return 'in_progress';
  return f.outcome === 'not_issue' ? 'not_issue' : 'completed';
};

export const PDV_FLAG_STATUS: Record<
  PdvFlagDisplay,
  { label: string; bg: string; fg: string; border?: string }
> = {
  pending: { label: 'Pending', bg: '#FCE8CF', fg: '#A35A00' },
  in_progress: { label: 'In progress', bg: '#EBF5FF', fg: '#037AE2' },
  completed: { label: 'Completed', bg: '#E6FAF0', fg: '#168A53' },
  not_issue: {
    label: 'Not an issue',
    bg: '#FFFFFF',
    fg: '#5D7083',
    border: '#AFBAC6'
  }
};

/** Entries written before events existed carry only the status they left behind. */
const eventOf = (h: PdvFlagHistoryEntry) => {
  if (h.event) return h.event;
  if (h.status === 'pending') return 'reported';
  if (h.status === 'in_progress') return 'started';
  return 'completed';
};

/** The product team's view: every action, and whose flag it was. */
export const pdvAdminHistory = (
  f: PdvFlag,
  me: string
): { at: string; line: string }[] => {
  const who = (email?: string | null) =>
    email && email === me ? 'You' : (email ?? 'Someone');
  const was = (h: PdvFlagHistoryEntry) =>
    h.from && h.from !== h.by ? ` · was assigned to ${who(h.from)}` : '';

  return (f.history ?? []).map(h => {
    const by = who(h.by);
    switch (eventOf(h)) {
      case 'reported':
        return { at: h.at, line: `Reported by ${h.by}` };
      case 'assigned':
        return { at: h.at, line: `Assigned to ${who(h.to)}${was(h)}` };
      case 'unassigned':
        return { at: h.at, line: `${by} unassigned it` };
      case 'released':
        return { at: h.at, line: 'Released back to Pending' };
      case 'started':
        return {
          at: h.at,
          line: `${by} started it${h.from && h.from !== h.by ? ` · reassigned from ${who(h.from)}` : ''}`
        };
      case 'reopened':
        return { at: h.at, line: `${by} reopened it` };
      default:
        return {
          at: h.at,
          line:
            h.outcome === 'not_issue'
              ? `${by} closed it: ${pdvReasonLabel(h.reason)}${h.note ? ` · “${h.note}”` : ''}${was(h)}`
              : `${by} completed it${was(h)}`
        };
    }
  });
};

/**
 * The rep's view. Assignment is the product team's own bookkeeping, so it is left
 * out — except when it puts the report back in the queue.
 */
export const pdvRepHistory = (
  f: PdvFlag,
  me: string
): { at: string; line: string }[] =>
  (f.history ?? []).flatMap((h, i, all) => {
    switch (eventOf(h)) {
      case 'reported':
        return [
          {
            at: h.at,
            line: f.createdBy === me ? 'You reported it' : `Reported by ${h.by}`
          }
        ];
      case 'started':
        return [{ at: h.at, line: 'The product team started working on it' }];
      case 'completed':
        return [
          {
            at: h.at,
            line:
              h.outcome === 'not_issue'
                ? `Closed as not an issue: ${pdvReasonLabel(h.reason)}`
                : 'The product team completed it'
          }
        ];
      case 'reopened':
        return [{ at: h.at, line: 'Reopened' }];
      case 'released':
        return [{ at: h.at, line: 'Back in the queue' }];
      case 'unassigned':
        // Releasing an in-progress flag puts it back; unassigning a pending one
        // changes nothing a rep would notice.
        return h.status === 'pending' && all[i - 1]?.status === 'in_progress'
          ? [{ at: h.at, line: 'Back in the queue' }]
          : [];
      default:
        return [];
    }
  });

/** revops-api answers errors as { status, message }; 409 means someone got there first. */
export const pdvErrorMessage = (
  e: unknown,
  fallback = 'Something went wrong.'
) => {
  const res = (
    e as { response?: { status?: number; data?: { message?: string } } }
  )?.response;
  if (res?.status === 409)
    return 'Someone else changed this since you loaded it. Reload and try again.';
  return res?.data?.message || fallback;
};
